import React from "react"
import { graphql } from "gatsby"
import PageSeo from "../../components/global/PageSeo"
import { Helmet } from "react-helmet"

import Layout from "../../assets/styles/layout"
import SeoLinks from "../../components/global/SeoLinks"
import { prepareSchema } from "../../utils/seoUrls"
import { FooterContainer } from "../../components/global/common/containers"

import Testimonials from "../../components/global/Testimonials"
import Partners from "../../components/global/Partners"
import CallToAction from "../../components/global/CallToAction"
import Footer from "../../components/global/Footer"

import { HomeHeader } from "../../components/home-page/HomePageHeader"
import { WhyUs } from "../../components/home-page/WhyUs"
import { WhatWeDo } from "../../components/home-page/WhatWeDo"
import { SimpleSteps } from "../../components/home-page/SimpleSteps"

export const query = graphql`
  query ($id: String!) {
    wpPage(id: { eq: $id }) {
      seo {
        title
        metaDesc
        focuskw
        metaKeywords
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
          mediaDetails {
            width
            height
          }
        }
        twitterTitle
        twitterDescription
        twitterImage {
          altText
          sourceUrl
          srcSet
          mediaDetails {
            width
            height
          }
        }
        canonical
        cornerstone
        schema {
          articleType
          pageType
          raw
        }
      }
      acfCallToAction {
        ctaPersonPosition
        ctaPersonName
        ctaForm
        ctaImage {
          altText
          atttachement_pl {
            altTextPl
          }
          sourceUrl
          srcSet
          mediaDetails {
            width
            height
          }
        }
        ctaContactOptions {
          eMail
          phone
        }
        ctaButtons {
          ctaButton1 {
            url
            title
            target
          }
          ctaButton2 {
            target
            url
            title
          }
        }
        ctaIntro
        ctaTitle
      }
      acfHomepage {
        homeStepsTitle
        homeStepsButton {
          target
          title
          url
        }
        homeStepsList {
          header
          description
        }
        homeBannerTitle {
          textGray
          textGreen
        }
        intro
        homeBannerButtons {
          homeBannerButton2 {
            target
            title
            url
          }
          homeBannerButton1 {
            target
            title
            url
          }
        }
        homeWhatWeDoTitle {
          textGray
          textGreen
        }
        homeWhatWeDoDescription
        homeWhatWeDoList {
          header
          icon
          description
          fieldGroupName
          homeWhatWeDoListButton {
            target
            title
            url
          }
        }
        homeBannerImages {
          homeBannerImage2 {
            altText
            atttachement_pl {
              altTextPl
            }
            sourceUrl
            srcSet
            mediaDetails {
              width
              height
            }
          }
          homeBannerImage1 {
            altText
            atttachement_pl {
              altTextPl
            }
            sourceUrl
            srcSet
            mediaDetails {
              width
              height
            }
          }
        }
        homeBannerLogos {
          altText
          atttachement_pl {
            altTextPl
          }
          sourceUrl
          srcSet
          mediaDetails {
            width
            height
          }
          id
          localFile {
            publicURL
          }
        }
        indentedSectionTitle
        indentedSectionList {
          description
          header
        }
        indentedSectionImage {
          altText
          atttachement_pl {
            altTextPl
          }
          sourceUrl
          srcSet
          mediaDetails {
            width
            height
          }
        }
        indentedSectionButton {
          target
          title
          url
        }
        testimonialsTitle
        testimonials {
          ... on WpTestimonial {
            id
            acfSingleTestimonials {
              content
              testimonialname
              position
              link {
                target
                title
                url
              }
              logo {
                altText
                atttachement_pl {
                  altTextPl
                }
                sourceUrl
                srcSet
                mediaDetails {
                  width
                  height
                }
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FIXED, width: 160, outputPixelDensities: [1, 2], formats: [WEBP])
                  }
                  publicURL
                }
              }
              testimonialimage {
                altText
                atttachement_pl {
                  altTextPl
                }
                srcSet
                mediaDetails {
                  width
                  height
                }
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FIXED, width: 122, outputPixelDensities: [0.5, 1, 1.5, 2], formats: [WEBP])
                  }
                }
              }
              testimonialimage {
                altText
                atttachement_pl {
                  altTextPl
                }
                srcSet
                mediaDetails {
                  width
                  height
                }
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FIXED, width: 122, outputPixelDensities: [0.5, 1, 1.5, 2], formats: [WEBP])
                  }
                }
              }
            }
          }
        }
        homepageStructuredData
      }
    }
  }
`

const IndexPage = ({ pageContext, data: { wpPage } }) => {
  const { seo } = wpPage
  const homepageData = wpPage.acfHomepage
  const structuredData = homepageData.homepageStructuredData

  const withPageSeo = seo && process.env.GATSBY_SITE_ENV === "production"

  return (
    <Layout>
      {withPageSeo && (
        <PageSeo post={wpPage} pageContext={pageContext} structuredData={structuredData} />
      )}
      <SeoLinks pageContext={pageContext} />
      <Helmet>
        {structuredData && !withPageSeo && (
          <script type="application/ld+json">
            {prepareSchema(structuredData, pageContext)}
          </script>
        )}
      </Helmet>
      <HomeHeader data={wpPage.acfHomepage} pageContext={pageContext} />
      <Testimonials data={wpPage.acfHomepage} />
      <WhatWeDo data={wpPage.acfHomepage} />
      <SimpleSteps data={wpPage.acfHomepage} />
      <WhyUs data={wpPage.acfHomepage} />
      <Partners />
      <FooterContainer>
        <CallToAction data={wpPage.acfCallToAction} />
        <Footer />
      </FooterContainer>
    </Layout>
  )
}

export default IndexPage